// eslint-disable-next-line no-restricted-imports
import {
  UpdateClientByAdminDto_Input,
  MutationInput_ClientController_UpdateById_Input_PepConnectionType as PepConnectionTypeApi,
  MutationInput_ClientController_UpdateById_Input_RiskLevel,
  MutationInput_ClientController_UpdateById_Input_InformationUpdateFrequency,
  MutationInput_ClientController_UpdateById_Input_Tariff,
  MutationInput_ClientController_UpdateById_Input_Gender,
  MutationInput_ClientController_UpdateById_Input_Type,
  MutationInput_ClientController_UpdateById_Input_Classification,
} from 'api/generated/graphql';
import { RelationType } from 'api/types/entity';
import { UpdateClientParams } from 'api/types/params';
import { formatToISODate } from 'helpers';

import {
  parseIdentityDocumentParam,
  parseManagementTypeParam,
  parseServiceContractParam,
} from '../parseParam';

import {
  parseBiographyParam,
  parseCategoryTypeParam,
  parseCurrencyParam,
  parseDebtsParam,
  parseEconomicSectorTypeParam,
  parseFinancingSourcesParam,
  parseInformationUpdateFrequency,
  parseClientOpenAccountOrderParam,
  parseOrderSubmissionContactsParam,
  parseRiskLevel,
  parseFinancialPositionParam,
  parseKnowledgeAndExperienceParam,
} from './parseParam';
import { parseBusinessRelationshipParam } from './parseParam/parseBusinessRelationshipParam';
import { parseRiskProfileParam } from './parseParam/parseRiskProfileParam';
import { parseSaveSourcesOfIncomeParam } from './parseParam/parseSaveSourcesOfIncomeParam';

const relationType: Record<RelationType, PepConnectionTypeApi> = {
  [RelationType.not]: PepConnectionTypeApi.None,
  [RelationType.yesSpouses]: PepConnectionTypeApi.FamilyMember,
  [RelationType.yesIAm]: PepConnectionTypeApi.Self,
};

export const parseUpdateClientParams = (
  params: UpdateClientParams
): UpdateClientByAdminDto_Input => ({
  versionNumber: params.versionNumber,
  firstName: params.firstName,
  lastName: params.lastName,
  middleName: params.middleName,
  dateOfBirth: formatToISODate(params.dateOfBirth),
  gender: params.gender
    ? MutationInput_ClientController_UpdateById_Input_Gender[params.gender]
    : undefined,
  citizenships: params.citizenships,
  biography: params.biography
    ? parseBiographyParam(params.biography)
    : undefined,
  debts: params.debts ? parseDebtsParam(params.debts) : undefined,
  bankAccounts: params.bankAccounts,
  financingSources: params.financingSources
    ? parseFinancingSourcesParam(params.financingSources)
    : undefined,
  financialPosition: params.financialPosition
    ? parseFinancialPositionParam(params.financialPosition)
    : undefined,
  placeOfBirth: params.placeOfBirth,
  registrationAddress: params.registrationAddress,
  residentialAddress: params.residentialAddress,
  taxResidences: params.taxResidences,
  orderSubmissionContacts: parseOrderSubmissionContactsParam(
    params.orderSubmissionContacts
  ),
  connectedPepInfo: params.connectedPepInfo,
  reason: params.reason,

  pepConnectionType: relationType[params.pepConnection],

  withdrawalAccounts: params.withdrawalAccounts.map((p) => ({
    ...p,
    currency: parseCurrencyParam(p.currency),
  })),

  accreditedInvestorStatus: params.accreditedInvestorStatus,

  riskLevel:
    parseRiskLevel<MutationInput_ClientController_UpdateById_Input_RiskLevel>(
      params.riskLevel
    ),
  informationUpdateFrequency:
    parseInformationUpdateFrequency<MutationInput_ClientController_UpdateById_Input_InformationUpdateFrequency>(
      params.informationUpdateFrequency
    ),
  riskLevelReviewDate: params.riskLevelReviewDate
    ? formatToISODate(params.riskLevelReviewDate)
    : null,
  lastInformationUpdateDate: params.lastInformationUpdateDate
    ? formatToISODate(params.lastInformationUpdateDate)
    : null,
  lastOperationMonitoringResults: params.lastOperationMonitoringResults ?? null,
  usedServices: params.usedServices ?? null,

  code: params.code,
  centralDepositoryAccountNumber: params.centralDepositoryAccountNumber,
  centralDepositoryAccountNumberOpeningDate:
    params.centralDepositoryAccountNumberOpeningDate
      ? formatToISODate(params.centralDepositoryAccountNumberOpeningDate)
      : null,
  personalAccountNumber: params.personalAccountNumber,
  personalAccountOpeningDate: params.personalAccountOpeningDate
    ? formatToISODate(params.personalAccountOpeningDate)
    : params.personalAccountOpeningDate,
  personalAccountClosingDate: params.personalAccountClosingDate
    ? formatToISODate(params.personalAccountClosingDate)
    : null,
  personalAccountFreezingDate: params.personalAccountFreezingDate
    ? formatToISODate(params.personalAccountFreezingDate)
    : null,
  managerId: params.managerId,

  additionalInformationFileIds: params.additionalInformationFileIds,
  additionalDocumentFileIds: params.additionalDocumentFileIds,

  openAccountOrder: params.openAccountOrder
    ? parseClientOpenAccountOrderParam(params.openAccountOrder)
    : undefined,

  category: parseCategoryTypeParam(params.category),
  economicSector: parseEconomicSectorTypeParam(params.economicSector),
  type: params.clientType
    ? MutationInput_ClientController_UpdateById_Input_Type[params.clientType]
    : null,
  classification: params.classification
    ? MutationInput_ClientController_UpdateById_Input_Classification[
        params.classification
      ]
    : null,
  isInsider: params.isInsider,
  managementType: parseManagementTypeParam(params.managementType),
  isFinancialMonitoringSubject: params.isFinancialMonitoringSubject,
  isKazakhstanResident: params.isKazakhstanResident,
  taxBenefits: params.taxBenefits,

  serviceContract: parseServiceContractParam(params.serviceContract),

  identityDocument: parseIdentityDocumentParam(params.identityDocument),

  tariff: params.tariff
    ? MutationInput_ClientController_UpdateById_Input_Tariff[params.tariff]
    : undefined,

  businessRelationship: params.businessRelationship
    ? parseBusinessRelationshipParam(params.businessRelationship)
    : undefined,
  riskProfile: parseRiskProfileParam(params.riskProfile),
  W8BENForm: params.W8BENForm,

  knowledgeAndExperience: params.knowledgeAndExperience
    ? parseKnowledgeAndExperienceParam(params.knowledgeAndExperience)
    : undefined,

  incomeSources: params.sourcesOfIncome
    ? parseSaveSourcesOfIncomeParam(params.sourcesOfIncome)
    : undefined,
  invoice: params.invoice
    ? {
        fileId: params.invoice.fileId ?? null,
        paymentConfirmationFileIds:
          params.invoice.paymentConfirmationFileIds ?? null,
      }
    : undefined,
  documentGroupsFileIds: params.documentGroupsFileIds,
  selfieFileIds: params.selfieFileIds,
  signaturesCardFileIds: params.signaturesCardFileIds,
  companySignedNoticeOfInvestmentAccountOpeningFileId:
    params.companySignedNoticeOfInvestmentAccountOpeningFileId,
  companySignedAgreementFileId_Finance:
    params.companySignedAgreementFileId_Finance,
  signedApplicationStandardAgreementFileId_Finance:
    params.signedApplicationStandardAgreementFileId_Finance,
  signedOpenSubAccountOrderFileId_Finance:
    params.signedOpenSubAccountOrderFileId_Finance,
});
